<script setup lang="ts">
/**
 * WorkflowAssociation
 *
 * Thin form-system wrapper that injects the workflow context and forwards
 * the relevant props to HousingContactActivityAssociations.
 *
 * Registered in formComponentDict.ts as FormElementType.WORKFLOW_ASSOCIATION.
 */
import { computed, inject } from "vue";
import { workflowInjectKey } from "~/Compliance/composables/useWorkflow";
import type { WorkflowState } from "~/Compliance/composables/useWorkflow";
import type { FormElementBase } from "~/api";
import type { DefaultFormComponentProps } from "./FormComponentRenderer.vue";
import HousingContactActivityAssociations from "./HousingContactActivityAssociations.vue";

defineProps<DefaultFormComponentProps<FormElementBase>>();

const { getWorkflow } = inject(workflowInjectKey, {
  updateWorkflowStatus: async () => undefined,
  updateWorkflowStatusFromNameStamp: async () => undefined,
  getWorkflowStatus: () => "",
  getWorkflow: () => undefined,
}) as WorkflowState;

const workflow = computed(() => getWorkflow());

/**
 * Determine if the workflow's primary entity is a person/contact (Contact
 * Activity workflow) or a housing application (On Hold workflow). Only pass
 * the primary-contact props when the primary entity really is a person.
 */
const primaryEntityIsApplication = computed(() => {
  const defName = workflow.value?.entity?.entityDefinition?.name;
  return defName === "Housing Application";
});

const primaryContactGuid = computed(() =>
  primaryEntityIsApplication.value
    ? undefined
    : (workflow.value?.entityGuid ?? undefined),
);
const primaryContactName = computed(() =>
  primaryEntityIsApplication.value
    ? undefined
    : (workflow.value?.entity?.name ?? undefined),
);
</script>

<template>
  <div class="HousingContactActivityAssociationsFormComponent">
    <slot name="label" :element="modelValue" />
    <HousingContactActivityAssociations
      :workflow="workflow"
      :primary-contact-guid="primaryContactGuid"
      :primary-contact-name="primaryContactName"
      :read-only="isReadOnly"
    />
  </div>
</template>

<style lang="less">
.HousingContactActivityAssociationsFormComponent {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
</style>
